import { getBorderWoodPattern, getWoodPattern, roundedRect } from '@bn-games/render-helpers';
import { seatWind, type Wind } from '../game/seatWinds.js';
import { SEATS, type Seat } from '../game/types.js';
import { seatPosition, type LayoutConfig, type Rect, type SeatPosition } from '../layout/types.js';
import {
  COMPASS_CENTER_HALF_PX,
  COMPASS_FONT_PX,
  COMPASS_RADIUS_PX,
  NUMERAL_FONT_STACK,
  POND_CORNER_RADIUS,
  POND_RECESS_EDGE_PX,
  TABLE_BORDER_WIDTH,
  TABLE_CORNER_RADIUS,
} from '../utils/constants.js';
import type { RenderPalette } from './palette.js';

const TABLE_BORDER_COLOR = 'rgba(0, 0, 0, 0.35)';

/**
 * The wood table: a full-bleed border-wood backdrop, a centred board-wood
 * square with a thin border, and the recessed central pond floor — the
 * "tiles zone" where discards land. Drawn entirely in SCREEN space, exactly
 * like domino's board + chain-region recess: the table furniture never pans
 * or zooms, only the discard TILES sitting on the pond floor do (see
 * `SceneRender.renderScene`). Pure — reads only the layout rects and palette
 * tokens; `getWoodPattern`/`getBorderWoodPattern` cache their generated
 * texture per (palette, dims) so this is cheap to call every frame.
 */
export function drawTable(
  ctx: CanvasRenderingContext2D,
  layout: LayoutConfig,
  palette: RenderPalette,
): void {
  const { viewport, table, pond } = layout;
  ctx.save();

  ctx.fillStyle = getBorderWoodPattern(ctx, palette.wood, {
    width: viewport.width,
    height: viewport.height,
  });
  ctx.fillRect(0, 0, viewport.width, viewport.height);

  roundedRect(ctx, table.x, table.y, table.width, table.height, TABLE_CORNER_RADIUS);
  ctx.fillStyle = getWoodPattern(ctx, palette.wood, { width: table.width, height: table.height });
  ctx.fill();

  ctx.strokeStyle = TABLE_BORDER_COLOR;
  ctx.lineWidth = TABLE_BORDER_WIDTH;
  roundedRect(ctx, table.x, table.y, table.width, table.height, TABLE_CORNER_RADIUS);
  ctx.stroke();

  drawPondRecess(ctx, pond);

  ctx.restore();
}

/**
 * Sunken-cavity bezel around the pond, cut into the board wood — dark band on
 * the top/left interior edge (light source from upper-left casts shadow into
 * the recess), subtle highlight on the bottom/right. Ported from domino's
 * `drawChainRegionRecess`: together with the wood surface it draws over, the
 * pond reads as physically lower than the racks around it, the same way
 * domino's chain region reads as sunk below the surrounding hand trays.
 */
function drawPondRecess(ctx: CanvasRenderingContext2D, region: Rect): void {
  const { x, y, width: w, height: h } = region;
  const edge = POND_RECESS_EDGE_PX;
  ctx.save();

  roundedRect(ctx, x, y, w, h, POND_CORNER_RADIUS);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.24)';
  ctx.fill();

  ctx.save();
  roundedRect(ctx, x, y, w, h, POND_CORNER_RADIUS);
  ctx.clip();

  const shadowTop = ctx.createLinearGradient(x, y, x, y + edge);
  shadowTop.addColorStop(0, 'rgba(0, 0, 0, 0.55)');
  shadowTop.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = shadowTop;
  ctx.fillRect(x, y, w, edge);

  const shadowLeft = ctx.createLinearGradient(x, y, x + edge, y);
  shadowLeft.addColorStop(0, 'rgba(0, 0, 0, 0.55)');
  shadowLeft.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = shadowLeft;
  ctx.fillRect(x, y, edge, h);

  const highlightBottom = ctx.createLinearGradient(x, y + h - edge, x, y + h);
  highlightBottom.addColorStop(0, 'rgba(255, 220, 160, 0)');
  highlightBottom.addColorStop(1, 'rgba(255, 220, 160, 0.14)');
  ctx.fillStyle = highlightBottom;
  ctx.fillRect(x, y + h - edge, w, edge);

  const highlightRight = ctx.createLinearGradient(x + w - edge, y, x + w, y);
  highlightRight.addColorStop(0, 'rgba(255, 220, 160, 0)');
  highlightRight.addColorStop(1, 'rgba(255, 220, 160, 0.14)');
  ctx.fillStyle = highlightRight;
  ctx.fillRect(x + w - edge, y, edge, h);

  ctx.restore();

  ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.lineWidth = 1;
  roundedRect(ctx, x, y, w, h, POND_CORNER_RADIUS);
  ctx.stroke();

  ctx.restore();
}

/**
 * Compass letter per SEAT. Seat 0 is always East per the variant's rules, so
 * this tracks seat winds, not real-world geography — turn order runs East →
 * South → West → North COUNTERCLOCKWISE on screen, matching `SEAT_POSITIONS`
 * and real mahjong's standard seating convention (see that file's doc
 * comment). The letters travel with their seats under POV rotation, so the
 * viewer's own wind is whatever sits at their bottom edge.
 */
/** Keyed by the WIND a seat holds, not by its index — the two only coincide
 *  while the dealer is seat 0. See `game/seatWinds.ts`. */
const COMPASS_LETTER = {
  east: 'E',
  south: 'S',
  west: 'W',
  north: 'N',
} as const satisfies Record<Wind, string>;

interface CompassOffset {
  readonly dx: number;
  readonly dy: number;
}

const COMPASS_OFFSET: Readonly<Record<SeatPosition, CompassOffset>> = {
  bottom: { dx: 0, dy: COMPASS_RADIUS_PX },
  top: { dx: 0, dy: -COMPASS_RADIUS_PX },
  left: { dx: -COMPASS_RADIUS_PX, dy: 0 },
  right: { dx: COMPASS_RADIUS_PX, dy: 0 },
};

/**
 * A small seat compass at the pond centre: four wind letters around a diamond
 * marker, with the letter for the seat whose turn it is lit in the accent ink.
 * Each letter sits at its seat's `localSeat`-relative screen edge, so the
 * viewer always reads their own wind at the bottom.
 *
 * `dealer` is what decides *which* wind each seat holds — Classical rotates it
 * hand to hand, so a compass keyed on the seat index alone would disagree with
 * the match HUD and with what the hand actually scores as.
 */
export function drawCompass(
  ctx: CanvasRenderingContext2D,
  layout: LayoutConfig,
  palette: RenderPalette,
  turn: Seat,
  localSeat: Seat,
  dealer: Seat,
): void {
  const cx = layout.pond.x + layout.pond.width / 2;
  const cy = layout.pond.y + layout.pond.height / 2;
  ctx.save();

  ctx.beginPath();
  ctx.moveTo(cx, cy - COMPASS_CENTER_HALF_PX);
  ctx.lineTo(cx + COMPASS_CENTER_HALF_PX, cy);
  ctx.lineTo(cx, cy + COMPASS_CENTER_HALF_PX);
  ctx.lineTo(cx - COMPASS_CENTER_HALF_PX, cy);
  ctx.closePath();
  ctx.fillStyle = palette.compassText;
  ctx.fill();

  ctx.font = `700 ${COMPASS_FONT_PX}px ${NUMERAL_FONT_STACK}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const seat of SEATS) {
    const offset = COMPASS_OFFSET[seatPosition(seat, localSeat)];
    const active = seat === turn;
    ctx.fillStyle = active ? palette.selectedRing : palette.compassText;
    if (active) {
      ctx.shadowColor = palette.claimGlow;
      ctx.shadowBlur = COMPASS_FONT_PX;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.fillText(COMPASS_LETTER[seatWind(seat, dealer)], cx + offset.dx, cy + offset.dy);
  }

  ctx.restore();
}
